/**
 * ImagingBucket — Ordering Engine (7D.2)
 */
import React from 'react';
import { api } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  ImageIcon, Plus, X, Check, Loader2, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { BucketHeader, EmptyBucket } from './shared';

/* ═══════════════════════════════════════
   IMAGING BUCKET — Ordering Engine (7D.2)
   ═══════════════════════════════════════ */

export function ImagingBucket({ enc, encounterId, fetchEncounter, outputs }) {
  const [imagingOrders, setImagingOrders] = React.useState(enc?.imaging_orders || []);
  const [showCatalog, setShowCatalog] = React.useState(false);
  const [modality, setModality] = React.useState('');
  const [catalogResults, setCatalogResults] = React.useState([]);
  const [selectedStudy, setSelectedStudy] = React.useState(null);
  const [laterality, setLaterality] = React.useState('');
  const [indication, setIndication] = React.useState('');
  const [ordering, setOrdering] = React.useState(false);

  const problems = enc?.note?.problem_oriented_ap || [];
  const dxCodes = outputs?.billing_suggestion?.diagnosis_codes || [];
  const availableDx = problems.map((p, i) => ({ code: p.icd_code || `P${i+1}`, label: p.problem_name || p.icd_code || `Problem ${i+1}` }));
  dxCodes.forEach(c => { if (!availableDx.find(d => d.code === c)) availableDx.push({ code: c, label: c }); });

  React.useEffect(() => { setImagingOrders(enc?.imaging_orders || []); }, [enc]);

  React.useEffect(() => {
    if (!showCatalog) return;
    api.get(`/imaging-catalog${modality ? `?modality=${modality}` : ''}`).then(r => setCatalogResults(r.data.studies || [])).catch(() => {});
  }, [showCatalog, modality]);

  const handleOrder = async (dxCode) => {
    if (!selectedStudy || !dxCode) return;
    if (selectedStudy.laterality === 'required' && !laterality) { toast.error('Laterality required'); return; }
    setOrdering(true);
    try {
      const res = await api.post(`/encounters/${encounterId}/imaging-orders`, {
        study_code: selectedStudy.code, diagnosis_code: dxCode,
        indication, laterality, urgency: 'routine',
      });
      if (res.data.success) {
        toast.success(`Ordered: ${res.data.order.study_name}`);
        fetchEncounter();
        setSelectedStudy(null); setLaterality(''); setIndication('');
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setOrdering(false); }
  };

  const MODALITIES = [
    { value: '', label: 'All' }, { value: 'xray', label: 'X-Ray' }, { value: 'ct', label: 'CT' },
    { value: 'mri', label: 'MRI' }, { value: 'ultrasound', label: 'Ultrasound' }, { value: 'dexa', label: 'DEXA' },
  ];

  return (
    <div className="space-y-4" data-testid="finalize-imaging-bucket">
      <div className="flex items-center justify-between">
        <BucketHeader icon={ImageIcon} title="Imaging" count={imagingOrders.length}
          status={imagingOrders.length > 0 ? 'complete' : undefined} />
        <Button variant="outline" size="sm" className="h-7 text-[10px] gap-1"
          onClick={() => setShowCatalog(!showCatalog)} data-testid="imaging-order-btn">
          <Plus className="w-3 h-3" /> Order Study
        </Button>
      </div>

      {/* Study Picker */}
      {showCatalog && (
        <div className="rounded-lg border p-3 bg-muted/20 animate-in fade-in-0 slide-in-from-top-1 duration-100 space-y-3" data-testid="imaging-catalog-picker">
          <div className="flex gap-1">
            {MODALITIES.map(m => (
              <button key={m.value} onClick={() => setModality(m.value)}
                className={`px-2 py-1 rounded text-[10px] font-medium ${modality === m.value ? 'bg-foreground text-background' : 'bg-muted text-muted-foreground hover:text-foreground'}`}>
                {m.label}
              </button>
            ))}
          </div>
          <div className="max-h-40 overflow-y-auto space-y-0.5">
            {catalogResults.map(study => (
              <button key={study.code + study.name} onClick={() => setSelectedStudy(study)}
                className={`w-full text-left px-2 py-1.5 rounded text-xs flex items-center gap-2 ${selectedStudy?.code === study.code ? 'bg-primary/10 border border-primary/20' : 'hover:bg-accent'}`}
                data-testid={`imaging-study-${study.code}`}>
                <Badge variant="outline" className="text-[8px] font-mono shrink-0">{study.code}</Badge>
                <span className="flex-1">{study.name}</span>
                {study.laterality === 'required' && <span className="text-[9px] text-amber-600">L/R</span>}
              </button>
            ))}
          </div>

          {/* Order form */}
          {selectedStudy && (
            <div className="border-t pt-2 space-y-2">
              <p className="text-xs font-medium">{selectedStudy.name}</p>
              {selectedStudy.laterality === 'required' && (
                <div className="flex gap-1">
                  {['left', 'right', 'bilateral'].map(l => (
                    <button key={l} onClick={() => setLaterality(l)}
                      className={`px-2 py-1 rounded text-[10px] font-medium capitalize ${laterality === l ? 'bg-foreground text-background' : 'bg-muted text-muted-foreground'}`}>
                      {l}
                    </button>
                  ))}
                </div>
              )}
              <input type="text" placeholder="Indication / reason..." value={indication}
                onChange={e => setIndication(e.target.value)}
                className="w-full text-xs border rounded px-2 py-1.5 bg-background" />
              {availableDx.length > 0 ? (
                <select className="w-full text-xs border rounded px-2 py-1.5 bg-background"
                  defaultValue="" onChange={e => { if (e.target.value) handleOrder(e.target.value); }}
                  data-testid="imaging-dx-select">
                  <option value="">Select diagnosis & order</option>
                  {availableDx.map(d => <option key={d.code} value={d.code}>{d.code} — {d.label}</option>)}
                </select>
              ) : <span className="text-[9px] text-amber-600">No diagnoses available</span>}
            </div>
          )}
        </div>
      )}

      {/* Existing imaging orders */}
      {imagingOrders.length > 0 && (
        <div className="space-y-1.5">
          {imagingOrders.map((order, i) => (
            <div key={order.id} className="rounded-md border p-2.5 bg-emerald-50/30" data-testid={`imaging-order-${i}`}>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-[8px] font-mono bg-emerald-50 text-emerald-700">{order.study_code}</Badge>
                <span className="text-xs font-medium">{order.study_name}</span>
                {order.laterality && <Badge variant="outline" className="text-[8px] capitalize">{order.laterality}</Badge>}
                <Badge className="text-[8px] bg-emerald-100 text-emerald-700 ml-auto">Ordered</Badge>
              </div>
              {order.indication && <p className="text-[10px] text-muted-foreground mt-1">Indication: {order.indication}</p>}
              <div className="flex items-center gap-1.5 mt-1">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                <Badge variant="outline" className="text-[8px] font-mono">{order.linked_diagnosis}</Badge>
              </div>
            </div>
          ))}
        </div>
      )}

      {imagingOrders.length === 0 && !showCatalog && (
        <EmptyBucket icon={ImageIcon} message="No imaging orders — use button above to add" />
      )}
    </div>
  );
}


